import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { ChatDrawer } from '@/components/common/ChatDrawer';
import { PublicHeader } from '@/components/marketplace/PublicHeader';

import { RatingStars } from '@/components/marketplace/RatingStars';
import { useAdLightbox } from '@/components/marketplace/useAdLightbox';
import { AdQRShareModal } from '@/components/marketplace/AdQRShareModal';
import { AdEmbedModal } from '@/components/marketplace/AdEmbedModal';
import { ProductRatingSummary } from '@/components/marketplace/ProductRatingSummary';
import { SimilarProductsCarousel } from '@/components/marketplace/SimilarProductsCarousel';
import { SizeGuideModal } from '@/components/marketplace/SizeGuideModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Heart, Share2, MapPin, Eye, Truck, ShieldCheck, MessageCircle, ChevronLeft, ChevronRight, QrCode, Code2, UserPlus, UserCheck, Ruler, ShieldAlert, Package as PackageIcon, Star, ArrowLeft, Store, ShoppingBag, ShoppingCart, Flag, Handshake, Gavel, ThumbsUp, ThumbsDown, Camera, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { AuctionPanel } from '@/components/marketplace/AuctionPanel';
import { ReportModal } from '@/components/common/ReportModal';
import { MakeOfferModal } from '@/components/common/MakeOfferModal';
import { ProBadge } from '@/components/common/ProBadge';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useShare } from '@/hooks/useShare';
import { useCart } from '@/context/CartContext';
import { AddedToCartModal } from '@/components/marketplace/AddedToCartModal';
import { SaveToListModal } from '@/components/marketplace/SaveToListModal';
import { SharePanel } from '@/components/common/SharePanel';
import { HtmlContent } from '@/components/common/HtmlContent';
import { OtherSellersOfProduct } from '@/components/marketplace/OtherSellersOfProduct';
import { SEOMeta } from '@/components/common/SEOMeta';

export default function AdDetailPage() {
    const { id } = useParams();
    const { user, role } = useAuth();
    const promptLogin = usePromptLogin();
    const nav = useNavigate();
    const { buildShareUrl } = useShareConfig();
    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mediaIdx, setMediaIdx] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [comments, setComments] = useState([]);
    const [qText, setQText] = useState('');
    const [cText, setCText] = useState('');
    const [buyDialog, setBuyDialog] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [embedOpen, setEmbedOpen] = useState(false);
    const [cartModalOpen, setCartModalOpen] = useState(false);
    const [listModalOpen, setListModalOpen] = useState(false);
    const { addItem } = useCart();
    const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
    const [settings, setSettings] = useState({ commission_pct: 10 });
    const [buying, setBuying] = useState(false);
    const [fav, setFav] = useState(false);
    const [following, setFollowing] = useState(false);
    const [reviews, setReviews] = useState({ reviews: [], summary: { total: 0, avg: 0, distribution: {} } });
    const [similar, setSimilar] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewStep, setReviewStep] = useState(1); // 1 = purchase question, 2 = rating form
    const [reviewPurchasedHere, setReviewPurchasedHere] = useState(null); // true / false
    const [reviewStars, setReviewStars] = useState(5);
    const [reviewTitle, setReviewTitle] = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [reviewPhotos, setReviewPhotos] = useState([]);
    const [reviewYoutubeUrl, setReviewYoutubeUrl] = useState('');
    const [reviewUploadingPhoto, setReviewUploadingPhoto] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [offerOpen, setOfferOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState('stripe'); // 'stripe' | 'in_person'

    const openReviewModal = () => {
        setReviewStep(1);
        setReviewPurchasedHere(null);
        setReviewStars(5);
        setReviewTitle('');
        setReviewComment('');
        setReviewPhotos([]);
        setReviewYoutubeUrl('');
        setReviewOpen(true);
    };

    const load = async () => {
        setLoading(true);
        const [a, q, c, s, r, sim] = await Promise.all([
            api.get(`/ads/${id}`),
            api.get(`/ads/${id}/questions`),
            api.get(`/ads/${id}/comments`),
            api.get('/public/settings'),
            api.get(`/ads/${id}/reviews`),
            api.get(`/ads/${id}/similar?limit=12`),
        ]);
        setAd(a.data);
        setFav(!!a.data.is_favorited);
        setFollowing(!!a.data.is_following_seller);
        setQuestions(q.data);
        setComments(c.data);
        setSettings({ commission_pct: s.data.commission_pct });
        setReviews(r.data);
        setSimilar(sim.data);
        // pre-select first shipping method
        if (a.data.shipping_methods?.length) setSelectedShipping(a.data.shipping_methods[0].id);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [id]);

    const { open: openLightbox, LightboxRoot } = useAdLightbox(ad?.media || []);

    const media = ad?.media || [];
    const current = media[mediaIdx];

    const primaryImage = useMemo(() => media.find(m => m.kind === 'image'), [media]);
    const isMine = user && ad && user.id === ad.seller_id;

    // Variant delta computation
    const activeVariantOptions = useMemo(() => {
        if (!ad?.variants) return [];
        return ad.variants.map(v => {
            const optId = selectedVariants[v.id];
            return v.options?.find(o => o.id === optId);
        }).filter(Boolean);
    }, [ad, selectedVariants]);

    const priceDelta = activeVariantOptions.reduce((s, o) => s + (o.price_delta || 0), 0);
    const finalPrice = ad ? ad.price + priceDelta : 0;
    const commission = Math.round(finalPrice * (settings.commission_pct / 100));
    const shippingCost = ad?.shipping_methods?.find(s => s.id === selectedShipping)?.cost || 0;
    const total = finalPrice + commission + shippingCost;

    // Effective stock: min stock across selected variants, or ad.stock
    const effectiveStock = useMemo(() => {
        if (activeVariantOptions.length > 0) {
            return Math.min(...activeVariantOptions.map(o => o.stock ?? 0));
        }
        return ad?.stock;
    }, [activeVariantOptions, ad]);

    const outOfStock = typeof effectiveStock === 'number' && effectiveStock <= 0;

    // Share robusto: funciona en iframe y en ventana directa
    const _adShareUrl = ad ? buildShareUrl(`/a/${ad.id}`) : '';
    const { share, panelOpen: sharePanelOpen, closePanel: closeSharePanel } = useShare(_adShareUrl, ad?.title || 'Ok lo Vendo');

    if (loading || !ad) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6">
                    <Skeleton className="aspect-[16/10] rounded-2xl mb-6" />
                    <Skeleton className="h-8 w-2/3 mb-2" />
                    <Skeleton className="h-5 w-1/3" />
                </main>
            </div>
        );
    }

    const askQuestion = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'comment' });
            if (!ok) return;
        }
        if (!qText.trim()) return;
        try {
            await api.post('/questions', { ad_id: ad.id, text: qText.trim() });
            toast.success(ad.moderation_qa ? 'Pregunta enviada. El vendedor la revisará antes de publicarla.' : 'Pregunta publicada');
            setQText('');
            const q = await api.get(`/ads/${id}/questions`); setQuestions(q.data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const addComment = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'comment' });
            if (!ok) return;
        }
        if (!cText.trim()) return;
        try {
            await api.post('/comments', { ad_id: ad.id, text: cText.trim() });
            toast.success(ad.moderation_comments ? 'Comentario enviado. El vendedor lo revisará antes de publicarlo.' : 'Comentario publicado');
            setCText('');
            const c = await api.get(`/ads/${id}/comments`); setComments(c.data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const startCheckout = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'pay' });
            if (!ok) return;
        }
        if (isMine) return toast.error('No puedes comprar tu propio anuncio');
        setBuying(true);
        try {
            const { data } = await api.post('/checkout', { ad_id: ad.id, origin_url: buildShareUrl('') || window.location.origin, delivery_method: deliveryMethod });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al iniciar pago');
            setBuying(false);
        }
    };

    const toggleFav = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'favorite' });
            if (!ok) return;
        }
        try {
            const { data } = await api.post('/favorites', { ad_id: ad.id });
            setFav(data.favorited);
        } catch { toast.error('Error'); }
    };

    const contactSeller = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'comment' });
            if (!ok) return;
        }
        if (!ad?.seller) return;
        // Try to open OkChat with a dedicated room for this buyer↔seller conversation
        if (typeof window.okChatOpen === 'function') {
            try {
                const { data } = await api.post('/okchat/room', {
                    seller_id: ad.seller.id,
                    ad_id: ad.id,
                    ad_title: ad.title,
                });
                window.okChatOpen({ room: { id: data.room_id, name: data.room_name } });
                return;
            } catch {
                // OkChat not configured — fall back to legacy ChatDrawer
            }
        }
        setChatOpen(true);
    };

    const toggleFollow = async () => {
        if (role !== 'user') { toast.error('Inicia sesión para seguir'); return nav('/login'); }
        try {
            const { data } = await api.post('/follow', { target_user_id: ad.seller.id });
            setFollowing(data.following);
            toast.success(data.following ? `Siguiendo a @${ad.seller.nickname}` : 'Dejaste de seguir');
        } catch { toast.error('Error'); }
    };

    const submitReview = async () => {
        if (reviewPurchasedHere === null) { toast.error('Indica si compraste este producto aquí'); return; }
        try {
            await api.post('/reviews', {
                ad_id: ad.id,
                stars: reviewStars,
                title: reviewTitle || null,
                comment: reviewComment || null,
                purchased_here: !!reviewPurchasedHere,
                photo_urls: reviewPhotos,
                youtube_url: reviewYoutubeUrl || null,
            });
            toast.success('Reseña publicada');
            setReviewOpen(false);
            const r = await api.get(`/ads/${id}/reviews`);
            setReviews(r.data);
            const a = await api.get(`/ads/${id}`);
            setAd(a.data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const handleReviewPhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setReviewUploadingPhoto(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const { data } = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            setReviewPhotos(prev => [...prev, data.url]);
        } catch { toast.error('Error al subir foto'); }
        finally { setReviewUploadingPhoto(false); e.target.value = ''; }
    };

    const extractYoutubeId = (url) => {
        if (!url) return null;
        const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
        return m ? m[1] : null;
    };

    const voteReview = async (reviewId, helpful) => {
        if (role !== 'user') { const ok = await promptLogin({ intent: 'comment' }); if (!ok) return; }
        try {
            const { data } = await api.post(`/reviews/${reviewId}/vote`, { helpful });
            setReviews(prev => ({
                ...prev,
                reviews: prev.reviews.map(r => r.id === reviewId
                    ? { ...r, helpful_count: data.helpful_count, not_helpful_count: data.not_helpful_count, my_vote: data.my_vote }
                    : r)
            }));
        } catch { toast.error('Error al votar'); }
    };

    const clothingCategories = ['ropa', 'muebles']; // categories where size guide makes sense (extend as needed)
    const showSizeGuide = ad.size_guide_slug || clothingCategories.includes(ad.category_slug);

    const seoImage = primaryImage ? mediaUrl(primaryImage.url) : undefined;
    const seoDescription = (() => {
        const plain = (ad.description || '').replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
        return `${plain ? plain + ' — ' : ''}${formatMoney(ad.price)}. Vendido por @${ad.seller?.nickname || 'vendedor'} en Ok lo Vendo.`;
    })();

    return (
        <div className="min-h-screen flex flex-col">
            <SEOMeta
                title={ad.title}
                description={seoDescription}
                image={seoImage}
                url={buildShareUrl(`/ad/${id}`)}
                type="product"
            />
            <PublicHeader />
            <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6">
                {/* Back button */}
                <div className="mb-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { if (window.history.length > 1) nav(-1); else nav('/'); }}
                        data-testid="ad-back-btn"
                        className="gap-2 hover:bg-red-50 hover:border-red-600 hover:text-red-700"
                    >
                        <ArrowLeft className="h-4 w-4" />Regresar
                    </Button>
                </div>

                {/* Seller card (moved above media - Phase 20g) */}
                {ad.seller && (
                    <div className="rounded-xl border p-3 mb-4" data-testid="ad-seller-card-top">
                        <div className="flex items-center gap-3">
                            <Link to={`/u/${ad.seller.nickname}`} className="flex items-center gap-3 flex-1 hover:opacity-80">
                                <div className="h-11 w-11 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold overflow-hidden">
                                    {ad.seller.avatar_url ? <img src={ad.seller.avatar_url} alt="" className="h-full w-full object-cover" /> : ad.seller.nickname[0].toUpperCase()}
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium flex items-center gap-1 flex-wrap">@{ad.seller.nickname}
                                        {ad.seller.verified && <ShieldCheck className="h-3.5 w-3.5 text-red-600" />}
                                        <VerifiedBadge verified={ad.seller.verified} />
                                        <ProBadge userId={ad.seller.id} />
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                                        <span className="inline-flex items-center gap-1"><RatingStars value={ad.seller.rating_avg} showValue /></span>
                                        <span>•</span>
                                        <span>{ad.seller.total_sales} ventas</span>
                                        <span>•</span>
                                        <span data-testid="seller-followers-count">{ad.seller.followers_count || 0} seguidores</span>
                                    </div>
                                </div>
                            </Link>
                            <div className="hidden md:flex items-center gap-2">
                                {role === 'user' && !isMine && (
                                    <Button size="sm" variant={following ? "outline" : "default"} onClick={toggleFollow} data-testid="follow-seller" className={following ? '' : 'bg-red-600 hover:bg-red-700'}>
                                        {following ? <><UserCheck className="h-3.5 w-3.5 mr-1" />Siguiendo</> : <><UserPlus className="h-3.5 w-3.5 mr-1" />Seguir</>}
                                    </Button>
                                )}
                                {role === 'user' && !isMine && (
                                    <Button variant="outline" size="sm" onClick={contactSeller} data-testid="contact-seller">
                                        <MessageCircle className="h-4 w-4 mr-1" />Contactar
                                    </Button>
                                )}
                                <Link to={`/u/${ad.seller.nickname}`}>
                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="seller-all-ads">
                                        <Store className="h-4 w-4 mr-1" />Todos mis anuncios
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        {/* Mobile actions */}
                        <div className="md:hidden mt-2 grid grid-cols-2 gap-2">
                            {role === 'user' && !isMine && (
                                <>
                                    <Button size="sm" variant={following ? "outline" : "default"} onClick={toggleFollow} className={following ? '' : 'bg-red-600 hover:bg-red-700'}>
                                        {following ? <><UserCheck className="h-3.5 w-3.5 mr-1" />Siguiendo</> : <><UserPlus className="h-3.5 w-3.5 mr-1" />Seguir</>}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={contactSeller}>
                                        <MessageCircle className="h-4 w-4 mr-1" />Contactar
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-12 gap-8">
                    {/* Left: media */}
                    <div className="col-span-12 lg:col-span-7">
                        <div
                            className={`relative aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-100 ${current?.kind === 'youtube' ? '' : 'cursor-zoom-in'}`}
                            data-testid="ad-detail-media-carousel"
                            onClick={() => { if (current?.kind !== 'youtube') openLightbox(mediaIdx); }}
                        >
                            {current?.kind === 'youtube' ? (
                                <iframe
                                    key={current.url}
                                    src={`https://www.youtube.com/embed/${current.url}?rel=0&autoplay=1&mute=1&playsinline=1&loop=1&playlist=${current.url}`}
                                    title="YouTube video"
                                    className="absolute inset-0 h-full w-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : current?.kind === 'video' ? (
                                <video key={current.url} controls src={mediaUrl(current.url)} className="absolute inset-0 h-full w-full object-contain bg-black" onClick={(e) => e.stopPropagation()} />
                            ) : current?.url ? (
                                <img src={mediaUrl(current.url)} alt={ad.title} className="absolute inset-0 h-full w-full object-contain" />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">Sin foto</div>
                            )}
                            {media.length > 1 && (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); setMediaIdx((mediaIdx - 1 + media.length) % media.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center shadow"><ChevronLeft className="h-5 w-5" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); setMediaIdx((mediaIdx + 1) % media.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center shadow"><ChevronRight className="h-5 w-5" /></button>
                                    <div className="absolute bottom-2 right-2 rounded-full bg-black/70 text-white text-xs px-2 py-0.5">{mediaIdx + 1} / {media.length}</div>
                                </>
                            )}
                        </div>
                        {media.length > 1 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto">
                                {media.map((m, i) => (
                                    <button key={m.id || i} onClick={() => setMediaIdx(i)} className={`h-16 w-16 shrink-0 rounded-lg overflow-hidden border-2 ${mediaIdx === i ? 'border-red-600' : 'border-transparent'}`}>
                                        {m.kind === 'youtube' ? (
                                            <img src={`https://img.youtube.com/vi/${m.url}/hqdefault.jpg`} alt="youtube" className="h-full w-full object-cover" />
                                        ) : m.kind === 'video' ? (
                                            <video src={mediaUrl(m.url)} className="h-full w-full object-cover" muted />
                                        ) : (
                                            <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right: info */}
                    <div className="col-span-12 lg:col-span-5">
                        <div className="sticky top-20 space-y-4">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                                <Badge variant="secondary" className="capitalize">{ad.condition?.replace('_', ' ')}</Badge>
                                {ad.shipping_available && <Badge variant="outline" className="gap-1"><Truck className="h-3 w-3" /> Envío</Badge>}
                                {ad.in_person_pickup_available && <Badge variant="outline" className="gap-1 border-emerald-400 text-emerald-700"><Handshake className="h-3 w-3" />Entrega en persona</Badge>}
                                <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {ad.views}</span>
                                {reviews.summary.total > 0 && (
                                    <a href="#reviews" className="inline-flex items-center gap-1 text-red-600 hover:underline">
                                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />{reviews.summary.avg.toFixed(1)} ({reviews.summary.total})
                                    </a>
                                )}
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{ad.title}</h1>
                            {/* Promo badges */}
                            {ad.promo_type && (
                                <div className="flex flex-wrap gap-2">
                                    {ad.promo_type === '2x1' && <Badge className="bg-red-600 text-white text-sm px-3 py-1">2×1 — Lleva 2, paga 1</Badge>}
                                    {ad.promo_type === '3x2' && <Badge className="bg-red-600 text-white text-sm px-3 py-1">3×2 — Lleva 3, paga 2</Badge>}
                                    {ad.promo_type === 'buy1_get2_discounted' && <Badge className="bg-orange-500 text-white text-sm px-3 py-1">2do artículo con {ad.promo_value || '??'}% OFF</Badge>}
                                </div>
                            )}
                            {/* Discount price: strike-through original, show current */}
                            {ad.discount_price && Number(ad.discount_price) > Number(finalPrice) && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-lg text-muted-foreground line-through">{formatMoney(ad.discount_price, ad.currency)}</span>
                                    <Badge className="bg-red-100 text-red-700 border-red-200 text-sm">
                                        {Math.round((1 - Number(finalPrice) / Number(ad.discount_price)) * 100)}% OFF
                                    </Badge>
                                </div>
                            )}
                            <div className="money text-3xl sm:text-4xl font-semibold text-red-600" data-testid="ad-detail-price">{formatMoney(finalPrice, ad.currency)}</div>
                            {typeof effectiveStock === 'number' && (
                                <div className={`text-sm inline-flex items-center gap-1 ${outOfStock ? 'text-red-600' : 'text-green-700'}`} data-testid="ad-stock">
                                    <PackageIcon className="h-4 w-4" />
                                    {outOfStock ? 'Sin stock' : `Stock disponible: ${effectiveStock}`}
                                </div>
                            )}
                            <div className="text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{ad.location}</div>

                            {/* Variants */}
                            {ad.variants?.length > 0 && (
                                <div className="space-y-3">
                                    {ad.variants.map(v => (
                                        <div key={v.id}>
                                            <div className="text-xs font-medium text-foreground mb-1.5">{v.name}: <span className="text-muted-foreground">{v.options.find(o => o.id === selectedVariants[v.id])?.name || 'Selecciona'}</span></div>
                                            <div className="flex flex-wrap gap-2">
                                                {v.options.map(o => {
                                                    const active = selectedVariants[v.id] === o.id;
                                                    const disabled = (o.stock ?? 0) <= 0;
                                                    if (v.kind === 'color') {
                                                        return (
                                                            <button key={o.id} onClick={() => !disabled && setSelectedVariants({ ...selectedVariants, [v.id]: o.id })}
                                                                title={o.name} disabled={disabled}
                                                                className={`h-9 w-9 rounded-full border-2 ${active ? 'border-red-600 ring-2 ring-red-200' : 'border-neutral-300'} ${disabled ? 'opacity-30 cursor-not-allowed line-through' : ''}`}
                                                                style={{ background: o.value || '#eee' }}
                                                                data-testid={`variant-color-${o.id}`}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <button key={o.id} onClick={() => !disabled && setSelectedVariants({ ...selectedVariants, [v.id]: o.id })}
                                                            disabled={disabled}
                                                            className={`min-w-11 h-9 px-3 rounded-md border-2 text-sm font-medium ${active ? 'border-red-600 bg-red-50 text-red-700' : 'border-neutral-300 hover:border-neutral-500'} ${disabled ? 'opacity-30 cursor-not-allowed line-through' : ''}`}
                                                            data-testid={`variant-option-${o.id}`}
                                                        >
                                                            {o.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    {showSizeGuide && (
                                        <button onClick={() => setSizeGuideOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:underline mt-1" data-testid="open-size-guide">
                                            <Ruler className="h-3.5 w-3.5" />Ver guía de tallas
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Shipping methods redesigned (Phase 20g) - HIDDEN in Phase 20i (moved below) */}
                            <div className="hidden" data-testid="ad-shipping-card">
                                <div className="px-3 py-2 bg-gradient-to-r from-slate-900 to-neutral-800 text-white flex items-center gap-2">
                                    <Truck className="h-4 w-4" />
                                    <span className="text-sm font-semibold">Envío con OkEnvíos</span>
                                    {ad.shipping_available && <Badge className="ml-auto bg-emerald-500/20 text-emerald-200 border-emerald-400/30 text-[10px]">Disponible</Badge>}
                                </div>
                                <div className="p-3 space-y-2">
                                    {ad.shipping_methods?.length > 0 ? (
                                        ad.shipping_methods.map(s => (
                                            <label key={s.id} className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${selectedShipping === s.id ? 'border-red-600 bg-red-50' : 'hover:border-neutral-400'}`}>
                                                <input type="radio" name="shipping" checked={selectedShipping === s.id} onChange={() => setSelectedShipping(s.id)} className="accent-red-600" data-testid={`shipping-${s.id}`} />
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">{s.name}</div>
                                                    {s.description && <div className="text-[10px] text-muted-foreground">{s.description}</div>}
                                                    {(s.days_min || s.days_max) && <div className="text-[10px] text-muted-foreground">{s.days_min && s.days_max ? `${s.days_min}-${s.days_max} días` : `${s.days_min || s.days_max} días`}</div>}
                                                </div>
                                                <div className={`text-sm font-semibold tabular-nums ${(s.free || s.cost === 0) ? 'text-green-700' : ''}`}>{(s.free || s.cost === 0) ? 'Gratis' : formatMoney(s.cost, ad.currency)}</div>
                                            </label>
                                        ))
                                    ) : (
                                        <p className="text-xs text-muted-foreground">Envío coordinado con el vendedor. Cotiza al comprar.</p>
                                    )}
                                    {/* Carriers strip */}
                                    <div className="pt-2 border-t">
                                        <div className="text-[10px] uppercase text-muted-foreground tracking-wide mb-1.5">Paqueterías disponibles</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {[
                                                { name: 'DHL', bg: 'bg-yellow-400', text: 'text-red-700', font: 'font-black' },
                                                { name: 'FedEx', bg: 'bg-white border', text: 'text-purple-700', font: 'font-black italic' },
                                                { name: 'Estafeta', bg: 'bg-red-600', text: 'text-white', font: 'font-bold' },
                                                { name: 'Redpack', bg: 'bg-red-700', text: 'text-white', font: 'font-bold' },
                                                { name: 'Paquetexpress', bg: 'bg-orange-500', text: 'text-white', font: 'font-bold' },
                                                { name: 'Sendex', bg: 'bg-blue-600', text: 'text-white', font: 'font-bold' },
                                            ].map(c => (
                                                <div key={c.name} className={`px-2.5 py-1 rounded ${c.bg} ${c.text} ${c.font} text-[10px] tracking-wide shadow-sm`}>
                                                    {c.name.toUpperCase()}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* In-person pickup instructions */}
                            {ad.in_person_pickup_available && (
                                <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 space-y-1.5" data-testid="inperson-pickup-card">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                                        <Handshake className="h-4 w-4" />Entrega en persona disponible
                                    </div>
                                </div>
                            )}

                            {/* Cashback banner (Phase 20g) - full width */}
                            {(ad.cashback_percent || ad.cashback_amount) && (() => {
                                const pct = Number(ad.cashback_percent) || 0;
                                const cashAmt = ad.cashback_amount != null ? Number(ad.cashback_amount) : (pct > 0 ? (Number(ad.price) || 0) * pct / 100 : 0);
                                return (
                                    <div className="rounded-xl overflow-hidden shadow-md" data-testid="ad-cashback-banner">
                                        <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-800 p-3 flex items-center gap-3">
                                            <img
                                                src="/assets/cashbacklogo.png"
                                                alt="Cashback"
                                                className="h-14 w-auto object-contain drop-shadow-md shrink-0"
                                                draggable={false}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white/90 text-[11px] uppercase tracking-widest font-semibold">Obtienes cashback</div>
                                                <div className="text-white flex items-baseline gap-2 flex-wrap">
                                                    {pct > 0 && <span className="text-2xl font-black tabular-nums">{pct}%</span>}
                                                    {cashAmt > 0 && <span className="text-lg font-bold tabular-nums">= {formatMoney(cashAmt, ad.currency)}</span>}
                                                </div>
                                                <div className="text-white/80 text-[11px]">Se acredita a tu OkWallet al confirmar recepción.</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="rounded-xl border p-3 text-sm bg-card">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-4 w-4 text-red-600" />
                                    <span className="font-medium">Pago protegido con Stripe (Escrow)</span>
                                </div>
                                <p className="mt-1 text-muted-foreground text-xs">Tu pago se libera al vendedor solo cuando confirmes la recepción.</p>
                                <div className="mt-2 pt-2 border-t flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] uppercase text-muted-foreground tracking-wide">Aceptamos</span>
                                    {/* Card brand pills */}
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <div className="px-2 py-1 rounded bg-[#1a1f71] text-white font-black italic text-[10px] tracking-wide shadow-sm" title="Visa">VISA</div>
                                        <div className="px-2 py-1 rounded bg-white border shadow-sm inline-flex items-center gap-0.5" title="Mastercard">
                                            <span className="h-3 w-3 rounded-full bg-[#eb001b] inline-block" />
                                            <span className="h-3 w-3 rounded-full bg-[#f79e1b] inline-block -ml-1.5 opacity-90" />
                                        </div>
                                        <div className="px-2 py-1 rounded bg-[#016fd0] text-white font-black text-[9px] tracking-tight shadow-sm" title="American Express">AMEX</div>
                                        <div className="px-2 py-1 rounded bg-black text-white font-medium text-[10px] shadow-sm inline-flex items-center gap-0.5" title="Apple Pay">
                                            <span className="text-[11px] leading-none"></span>
                                            <span>Pay</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Warranty */}
                            {ad.warranty?.enabled && (
                                <div className="rounded-xl border-2 border-red-600 p-3 bg-red-50 text-sm" data-testid="ad-warranty">
                                    <div className="flex items-center gap-2">
                                        <ShieldAlert className="h-4 w-4 text-red-600" />
                                        <span className="font-semibold">{ad.warranty.title || `Garantía ${ad.warranty.months} meses`}</span>
                                    </div>
                                    {ad.warranty.terms && <p className="mt-1.5 text-xs text-red-900 whitespace-pre-wrap">{ad.warranty.terms}</p>}
                                </div>
                            )}

                            <div className="space-y-2">
                                {ad.sale_mode === 'auction' ? (
                                    <AuctionPanel ad={ad} />
                                ) : (
                                    <Button className="w-full bg-red-600 hover:bg-red-700" size="lg" onClick={() => setBuyDialog(true)} disabled={isMine || ad.status === 'sold' || outOfStock} data-testid="ad-detail-buy-button">
                                        {ad.status === 'sold' ? 'Vendido' : outOfStock ? 'Sin stock' : isMine ? 'Es tu anuncio' : 'Comprar ahora'}
                                    </Button>
                                )}
                                {/* Agregar al carrito — naranja, solo si no está vendido ni sin stock */}
                                {ad.sale_mode !== 'auction' && !isMine && ad.status !== 'sold' && !outOfStock && (
                                    <button
                                        onClick={() => { addItem(ad); setCartModalOpen(true); }}
                                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold py-3 transition-colors"
                                        data-testid="ad-detail-add-to-cart"
                                    >
                                        <ShoppingCart className="h-5 w-5" />
                                        Agregar al carrito
                                    </button>
                                )}
                                {/* Guardar en lista */}
                                {!isMine && (
                                    <button
                                        onClick={() => {
                                            if (role !== 'user') { promptLogin({ intent: 'save' }); return; }
                                            setListModalOpen(true);
                                        }}
                                        className="w-full flex items-center justify-center gap-2 rounded-lg border border-input bg-card hover:bg-secondary text-sm font-medium py-2.5 transition-colors"
                                        data-testid="ad-detail-save-to-list"
                                    >
                                        <Heart className="h-4 w-4 text-red-500" />
                                        Guardar en lista
                                    </button>
                                )}
                                {(ad.sale_mode === 'offers' || ad.accepts_offers) && !isMine && ad.sale_mode !== 'auction' && (
                                    <Button variant="outline" className="w-full border-red-600 text-red-700 hover:bg-red-50" size="lg" onClick={async () => {
                                        if (role !== 'user') {
                                            const ok = await promptLogin({ intent: 'offer' });
                                            if (!ok) return;
                                        }
                                        setOfferOpen(true);
                                    }} data-testid="ad-detail-offer-button">
                                        <Handshake className="h-4 w-4 mr-2" />Hacer oferta
                                    </Button>
                                )}
                                <div className="grid grid-cols-4 gap-2">
                                    <button onClick={toggleFav} data-testid="ad-detail-fav" aria-label="Favorito"
                                        className={`h-10 rounded-lg border-2 border-red-600 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors ${fav && role === 'user' ? 'bg-red-600 text-white' : 'text-red-600 bg-white hover:bg-red-50'}`}>
                                        <Heart className={`h-4 w-4 ${fav && role === 'user' ? 'fill-white' : ''}`} />
                                        <span className="hidden sm:inline">Favorito</span>
                                    </button>
                                    <div className="relative">
                                        <button onClick={share} data-testid="ad-detail-share" aria-label="Compartir"
                                            className="w-full h-10 rounded-lg border-2 border-black text-black bg-white hover:bg-black hover:text-white flex items-center justify-center gap-1.5 text-xs font-medium transition-colors">
                                            <Share2 className="h-4 w-4" />
                                            <span className="hidden sm:inline">Compartir</span>
                                        </button>
                                        <SharePanel
                                            url={_adShareUrl}
                                            title={ad?.title || 'Ok lo Vendo'}
                                            isOpen={sharePanelOpen}
                                            onClose={closeSharePanel}
                                            className="bottom-full mb-1 left-0 right-0"
                                        />
                                    </div>
                                    <button onClick={() => setQrOpen(true)} data-testid="ad-detail-qr" aria-label="QR"
                                        className="h-10 rounded-lg border-2 border-red-600 text-red-600 bg-white hover:bg-red-600 hover:text-white flex items-center justify-center gap-1.5 text-xs font-medium transition-colors">
                                        <QrCode className="h-4 w-4" />
                                        <span className="hidden sm:inline">QR</span>
                                    </button>
                                    <button onClick={() => setEmbedOpen(true)} data-testid="ad-detail-embed" aria-label="Embed"
                                        className="h-10 rounded-lg border-2 border-black text-black bg-white hover:bg-black hover:text-white flex items-center justify-center gap-1.5 text-xs font-medium transition-colors">
                                        <Code2 className="h-4 w-4" />
                                        <span className="hidden sm:inline">Embed</span>
                                    </button>
                                </div>
                                {role === 'user' && !isMine && (
                                    <button onClick={() => setReportOpen(true)} className="w-full text-xs text-muted-foreground hover:text-red-600 flex items-center justify-center gap-1 py-1" data-testid="ad-detail-report">
                                        <Flag className="h-3 w-3" />Reportar este anuncio
                                    </button>
                                )}
                            </div>

                            {/* Seller card was moved above media (Phase 20g). */}
                        </div>
                    </div>
                </div>

                {/* Description */}
                <section className="mt-10">
                    <h2 className="text-xl font-semibold tracking-tight mb-2">Descripción</h2>
                    <HtmlContent html={ad.description} className="text-sm text-foreground/85" dataTestId="ad-description" />
                    {ad.tags?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {ad.tags.map(t => <Badge key={t} variant="secondary">#{t}</Badge>)}
                        </div>
                    )}
                </section>

                {/* Other sellers of the same product */}
                <OtherSellersOfProduct ad={ad} />

                {/* Similar products */}
                <SimilarProductsCarousel ads={similar} />

                {/* OKEnvíos banner — above ratings */}
                <section className="mt-10 rounded-2xl overflow-hidden shadow-xl" data-testid="okenvios-promo-banner">
                    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-neutral-900 text-white">
                        {/* Header row */}
                        <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-white/10">
                            <div className="h-11 w-11 rounded-xl bg-red-600 flex items-center justify-center shadow-lg shrink-0">
                                <Truck className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-base font-bold tracking-tight">OKEnvíos</div>
                                <div className="text-xs text-white/60">Envía con las mejores paqueterías de México · integrado con envia.com</div>
                            </div>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-3 py-1 text-[11px] font-semibold shrink-0">
                                <ShieldCheck className="h-3.5 w-3.5" /> Envío protegido
                            </span>
                        </div>
                        {/* Carriers grid */}
                        <div className="px-5 py-4">
                            <div className="text-[10px] uppercase tracking-widest text-white/35 mb-3">Paqueterías disponibles</div>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { name: 'DHL',            bg: '#FFCC00', color: '#CC0000' },
                                    { name: 'FedEx',          bg: '#4D148C', color: '#FF6200' },
                                    { name: 'Estafeta',       bg: '#C8102E', color: '#FFFFFF' },
                                    { name: 'Redpack',        bg: '#B80000', color: '#FFFFFF' },
                                    { name: 'Paquetexpress',  bg: '#FF6A00', color: '#FFFFFF' },
                                    { name: 'Sendex',         bg: '#0057A8', color: '#FFFFFF' },
                                    { name: 'J&T Express',    bg: '#E31837', color: '#FFFFFF' },
                                    { name: 'UPS',            bg: '#351C15', color: '#F4A900' },
                                    { name: '99minutos',      bg: '#00B4D8', color: '#FFFFFF' },
                                    { name: 'iVoy',           bg: '#6C63FF', color: '#FFFFFF' },
                                    { name: 'Skydropx',       bg: '#0EA5E9', color: '#FFFFFF' },
                                    { name: 'Borzo',          bg: '#FF4081', color: '#FFFFFF' },
                                    { name: 'Carssa',         bg: '#1565C0', color: '#FFFFFF' },
                                    { name: 'Quiken',         bg: '#2E7D32', color: '#FFFFFF' },
                                    { name: 'Tresguerras',    bg: '#6D4C41', color: '#FFFFFF' },
                                    { name: 'Hermes',         bg: '#37474F', color: '#FFFFFF' },
                                ].map(c => (
                                    <div
                                        key={c.name}
                                        className="px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-wide shadow-md"
                                        style={{ backgroundColor: c.bg, color: c.color }}
                                    >
                                        {c.name.toUpperCase()}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Product ratings */}
                <section id="reviews" className="mt-10 space-y-4">
                    {/* OK Califícalo brand logo */}
                    <div className="flex items-center justify-center pb-2">
                        <img
                            src="/assets/okcalificalo-logo.png"
                            alt="OK Califícalo"
                            className="h-14 sm:h-20 w-auto select-none"
                            data-testid="ok-calificalo-logo"
                            draggable={false}
                        />
                    </div>
                    <div className="flex items-baseline justify-between">
                        <h2 className="text-xl font-semibold tracking-tight">Calificaciones del producto</h2>
                        {role === 'user' && !isMine && (
                            <Button variant="outline" size="sm" onClick={openReviewModal} data-testid="open-review">
                                <Star className="h-4 w-4 mr-1" />Escribir reseña
                            </Button>
                        )}
                    </div>
                    <ProductRatingSummary summary={reviews.summary} />
                    <div className="space-y-3">
                        {reviews.reviews.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Aún no hay reseñas. ¡Sé el primero en calificar este producto!</p>
                        ) : reviews.reviews.map(r => (
                            <div key={r.id} className="rounded-xl border bg-card p-4" data-testid={`review-${r.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="h-8 w-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold overflow-hidden">
                                            {r.author?.avatar_url ? <img src={r.author.avatar_url} alt="" className="h-full w-full object-cover" /> : r.author?.nickname?.[0]?.toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">@{r.author?.nickname}</div>
                                            <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('es-MX')}</div>
                                        </div>
                                    </div>
                                    <RatingStars value={r.stars} />
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {r.purchased_here ? (
                                        <Badge variant="outline" className="border-emerald-600 text-emerald-700 gap-1" data-testid="badge-bought-here">
                                            <ShieldCheck className="h-3 w-3" />Comprado en Ok te lo Vendo
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="border-amber-500 text-amber-700 gap-1" data-testid="badge-bought-elsewhere">
                                            <ShoppingBag className="h-3 w-3" />Comprado en otro lugar
                                        </Badge>
                                    )}
                                    {r.verified_purchase && !r.purchased_here && (
                                        <Badge variant="outline" className="border-neutral-400 text-neutral-600 gap-1 text-[10px]">Compra verificada</Badge>
                                    )}
                                </div>
                                {r.title && <div className="mt-2 font-semibold text-sm">{r.title}</div>}
                                {r.comment && <p className="mt-1 text-sm text-foreground/85">{r.comment}</p>}
                                {/* Review photos */}
                                {r.photo_urls?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {r.photo_urls.map((url, i) => (
                                            <a key={i} href={mediaUrl(url)} target="_blank" rel="noopener noreferrer">
                                                <img src={mediaUrl(url)} alt="" className="h-20 w-20 rounded-lg object-cover border hover:opacity-90 transition-opacity" />
                                            </a>
                                        ))}
                                    </div>
                                )}
                                {/* YouTube link */}
                                {r.youtube_url && extractYoutubeId(r.youtube_url) && (
                                    <div className="mt-3">
                                        <a href={r.youtube_url} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-2 rounded-lg border p-2.5 hover:bg-neutral-50 transition-colors text-sm">
                                            <div className="h-8 w-8 rounded bg-red-600 flex items-center justify-center shrink-0">
                                                <span className="text-white text-[11px] font-bold">▶</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-xs">Video en YouTube</div>
                                                <div className="text-[11px] text-muted-foreground truncate">{r.youtube_url}</div>
                                            </div>
                                        </a>
                                    </div>
                                )}
                                {/* Helpful votes */}
                                <div className="mt-3 flex items-center gap-3 pt-2 border-t">
                                    <span className="text-[11px] text-muted-foreground">¿Fue útil?</span>
                                    <button
                                        onClick={() => voteReview(r.id, true)}
                                        className={`flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 border transition-colors ${r.my_vote === 'helpful' ? 'bg-emerald-50 border-emerald-400 text-emerald-700 font-semibold' : 'hover:bg-neutral-50 text-muted-foreground'}`}
                                        data-testid={`vote-helpful-${r.id}`}
                                    >
                                        <ThumbsUp className="h-3 w-3" />{r.helpful_count || 0}
                                    </button>
                                    <button
                                        onClick={() => voteReview(r.id, false)}
                                        className={`flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 border transition-colors ${r.my_vote === 'not_helpful' ? 'bg-red-50 border-red-400 text-red-700 font-semibold' : 'hover:bg-neutral-50 text-muted-foreground'}`}
                                        data-testid={`vote-not-helpful-${r.id}`}
                                    >
                                        <ThumbsDown className="h-3 w-3" />{r.not_helpful_count || 0}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Q&A + Comments */}
                <section className="mt-10">
                    {/* OK Coméntalo brand logo (Phase 20g) */}
                    <div className="flex items-center justify-center pb-3">
                        <img
                            src="/assets/okcomentalo-logo.png"
                            alt="OK Coméntalo"
                            className="h-12 sm:h-16 w-auto select-none"
                            data-testid="ok-comentalo-logo"
                            draggable={false}
                        />
                    </div>
                    <Tabs defaultValue="questions">
                        <TabsList>
                            <TabsTrigger value="questions" data-testid="tab-questions">Preguntas ({questions.length})</TabsTrigger>
                            <TabsTrigger value="comments" data-testid="tab-comments">Comentarios ({comments.length})</TabsTrigger>
                        </TabsList>
                        <TabsContent value="questions" className="mt-4">
                            <div className="space-y-3 mb-4">
                                <Textarea placeholder="Escribe una pregunta al vendedor..." value={qText} onChange={(e) => setQText(e.target.value)} data-testid="question-input" />
                                {ad.moderation_qa && <p className="text-xs text-muted-foreground">Este vendedor modera preguntas antes de publicarlas.</p>}
                                <Button onClick={askQuestion} className="bg-red-600 hover:bg-red-700" data-testid="question-submit"><MessageCircle className="h-4 w-4 mr-2" />Preguntar</Button>
                            </div>
                            <div className="space-y-3">
                                {questions.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay preguntas.</p>}
                                {questions.map(q => (
                                    <div key={q.id} className="rounded-xl border p-3">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="text-xs text-muted-foreground">@{q.asker?.nickname || 'usuario'}</div>
                                            {q.status === 'pending' && isMine && <Badge variant="outline" className="text-[10px]">Pendiente moderación</Badge>}
                                        </div>
                                        <div className="text-sm">{q.text}</div>
                                        {q.answer ? (
                                            <div className="mt-2 border-l-2 border-red-600 pl-3">
                                                <div className="text-xs text-muted-foreground">Respuesta del vendedor</div>
                                                <div className="text-sm">{q.answer}</div>
                                            </div>
                                        ) : isMine ? (
                                            <AnswerBox qid={q.id} status={q.status} onDone={load} />
                                        ) : (
                                            <div className="mt-2 text-xs text-muted-foreground italic">Sin respuesta aún</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </TabsContent>
                        <TabsContent value="comments" className="mt-4">
                            <div className="space-y-3 mb-4">
                                <Textarea placeholder="Deja un comentario..." value={cText} onChange={(e) => setCText(e.target.value)} data-testid="comment-input" />
                                {ad.moderation_comments && <p className="text-xs text-muted-foreground">Este vendedor modera los comentarios antes de publicarlos.</p>}
                                <Button onClick={addComment} className="bg-red-600 hover:bg-red-700" data-testid="comment-submit">Publicar comentario</Button>
                            </div>
                            <div className="space-y-3">
                                {comments.length === 0 && <p className="text-sm text-muted-foreground">Sé el primero en comentar.</p>}
                                {comments.map(c => (
                                    <div key={c.id} className="rounded-xl border p-3">
                                        <div className="flex items-center gap-2">
                                            <div className="text-xs text-muted-foreground">@{c.author?.nickname || 'usuario'}</div>
                                            {c.status === 'pending' && isMine && <Badge variant="outline" className="text-[10px]">Pendiente</Badge>}
                                        </div>
                                        <div className="text-sm mt-1">{c.text}</div>
                                    </div>
                                ))}
                            </div>
                        </TabsContent>
                    </Tabs>
                </section>
            </main>
            

            {LightboxRoot}

            <AdQRShareModal open={qrOpen} onOpenChange={setQrOpen} ad={ad} />
            <AdEmbedModal open={embedOpen} onOpenChange={setEmbedOpen} ad={ad} />
            <SizeGuideModal open={sizeGuideOpen} onOpenChange={setSizeGuideOpen} slug={ad.size_guide_slug || 'ropa_hombre'} />

            {/* Buy dialog */}
            <Dialog open={buyDialog} onOpenChange={setBuyDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Revisar y pagar</DialogTitle>
                        <DialogDescription>Confirma los detalles antes de proceder al pago con Stripe.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 text-sm">
                        {/* Delivery method selector */}
                        <div className="rounded-xl border bg-secondary/30 p-3 space-y-2">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Método de entrega</div>
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setDeliveryMethod('stripe')}
                                    className={`rounded-lg border-2 p-2.5 text-left text-xs transition-all ${deliveryMethod === 'stripe' ? 'border-red-500 bg-red-50' : 'border-transparent bg-card hover:border-border'}`}>
                                    <Truck className="h-4 w-4 mb-1 text-blue-600" />
                                    <div className="font-semibold">Envío</div>
                                    <div className="text-muted-foreground leading-tight">El vendedor envía a tu dirección</div>
                                </button>
                                <button type="button" onClick={() => setDeliveryMethod('in_person')}
                                    className={`rounded-lg border-2 p-2.5 text-left text-xs transition-all ${deliveryMethod === 'in_person' ? 'border-red-500 bg-red-50' : 'border-transparent bg-card hover:border-border'}`}>
                                    <Handshake className="h-4 w-4 mb-1 text-emerald-600" />
                                    <div className="font-semibold">Entrega en persona</div>
                                    <div className="text-muted-foreground leading-tight">Coordina punto de encuentro</div>
                                </button>
                            </div>
                            {deliveryMethod === 'in_person' && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 leading-snug">
                                    ⚠️ <strong>Okgo no se hace responsable de la liberación del pago una vez que el comprador haya decidido liberar el pago.</strong> El pago queda retenido hasta que tú lo liberes desde tu módulo de compras.
                                </div>
                            )}
                        </div>
                        <div className="flex justify-between"><span>Producto</span><span className="font-medium text-right max-w-[60%]">{ad.title}</span></div>
                        {activeVariantOptions.map(o => (
                            <div key={o.id} className="flex justify-between text-xs text-muted-foreground"><span>Variante</span><span>{o.name}</span></div>
                        ))}
                        <div className="flex justify-between"><span>Precio</span><span className="tabular-nums">{formatMoney(finalPrice, ad.currency)}</span></div>
                        <div className="flex justify-between"><span>Comisión ({settings.commission_pct}%)</span><span className="tabular-nums" data-testid="checkout-fee-amount">{formatMoney(commission, ad.currency)}</span></div>
                        {shippingCost > 0 && <div className="flex justify-between"><span>Envío</span><span className="tabular-nums">{formatMoney(shippingCost, ad.currency)}</span></div>}
                        <div className="h-px bg-border my-2" />
                        <div className="flex justify-between font-semibold text-base"><span>Total</span><span className="money text-red-600" data-testid="checkout-total-amount">{formatMoney(total, ad.currency)}</span></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBuyDialog(false)}>Cancelar</Button>
                        <Button onClick={startCheckout} disabled={buying} className="bg-red-600 hover:bg-red-700" data-testid="checkout-confirm-button">{buying ? 'Redirigiendo…' : 'Ir a pagar'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Product review dialog - 2 steps: purchase question then rating */}
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="review-modal">
                    <DialogHeader>
                        <div className="flex items-center justify-center pb-2">
                            <img
                                src="/assets/okcalificalo-logo.png"
                                alt="OK Califícalo"
                                className="h-10 sm:h-12 w-auto"
                                draggable={false}
                            />
                        </div>
                        <DialogTitle className="text-center">
                            {reviewStep === 1 ? 'Antes de calificar…' : 'Comparte tu experiencia'}
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            {reviewStep === 1
                                ? 'Necesitamos saber cómo obtuviste este producto para dar más credibilidad a tu reseña.'
                                : `Calificando: ${ad.title}`}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Step indicator */}
                    <div className="flex items-center justify-center gap-2 py-1">
                        <div className={`h-2 w-8 rounded-full transition-colors ${reviewStep >= 1 ? 'bg-red-600' : 'bg-neutral-200'}`} />
                        <div className={`h-2 w-8 rounded-full transition-colors ${reviewStep >= 2 ? 'bg-red-600' : 'bg-neutral-200'}`} />
                    </div>

                    {reviewStep === 1 && (
                        <div className="space-y-3 py-2">
                            <p className="text-sm font-medium text-center">¿Has comprado este producto?</p>
                            <button
                                onClick={() => { setReviewPurchasedHere(true); setReviewStep(2); }}
                                className="w-full flex items-start gap-3 rounded-2xl border-2 p-4 text-left hover:border-red-600 hover:bg-red-50 transition-colors"
                                data-testid="review-bought-here"
                            >
                                <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                                    <ShieldCheck className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold">Sí, lo compré aquí</div>
                                    <div className="text-xs text-muted-foreground">Realicé la compra en Ok te lo Vendo. Tu reseña se marcará como <b>Compra verificada</b>.</div>
                                </div>
                            </button>
                            <button
                                onClick={() => { setReviewPurchasedHere(false); setReviewStep(2); }}
                                className="w-full flex items-start gap-3 rounded-2xl border-2 p-4 text-left hover:border-red-600 hover:bg-red-50 transition-colors"
                                data-testid="review-bought-elsewhere"
                            >
                                <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                                    <ShoppingBag className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold">No lo compré aquí</div>
                                    <div className="text-xs text-muted-foreground">Lo obtuve en otro lugar, pero quiero compartir mi opinión del producto.</div>
                                </div>
                            </button>
                        </div>
                    )}

                    {reviewStep === 2 && (
                        <div className="space-y-3 py-2">
                            {/* selected purchase context badge */}
                            <div className="flex items-center justify-center">
                                {reviewPurchasedHere ? (
                                    <Badge variant="outline" className="border-emerald-600 text-emerald-700 gap-1"><ShieldCheck className="h-3 w-3" />Compra en Ok te lo Vendo</Badge>
                                ) : (
                                    <Badge variant="outline" className="border-amber-500 text-amber-700 gap-1"><ShoppingBag className="h-3 w-3" />Compra en otro lugar</Badge>
                                )}
                            </div>
                            <div className="flex items-center justify-center gap-1">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setReviewStars(n)}
                                        className={`h-10 w-10 text-3xl transition-transform hover:scale-110 ${n <= reviewStars ? 'text-amber-500' : 'text-neutral-300'}`}
                                        data-testid={`review-star-${n}`}
                                    >★</button>
                                ))}
                                <span className="ml-2 text-sm text-muted-foreground">{reviewStars}/5</span>
                            </div>
                            <input
                                className="w-full rounded-md border px-3 py-2 text-sm"
                                placeholder="Título (opcional, ej. Excelente producto)"
                                value={reviewTitle}
                                onChange={(e) => setReviewTitle(e.target.value)}
                                data-testid="review-title"
                                maxLength={140}
                            />
                            <Textarea
                                placeholder="Cuenta tu experiencia con el producto…"
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                rows={4}
                                maxLength={1500}
                                data-testid="review-comment"
                            />
                            {/* YouTube URL */}
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Video de YouTube (opcional)</label>
                                <input
                                    className="w-full rounded-md border px-3 py-2 text-sm"
                                    placeholder="https://youtube.com/watch?v=..."
                                    value={reviewYoutubeUrl}
                                    onChange={(e) => setReviewYoutubeUrl(e.target.value)}
                                    data-testid="review-youtube"
                                />
                            </div>
                            {/* Photo upload */}
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Fotos del producto (opcional, máx. 4)</label>
                                <div className="flex flex-wrap gap-2">
                                    {reviewPhotos.map((url, i) => (
                                        <div key={i} className="relative h-16 w-16">
                                            <img src={mediaUrl(url)} alt="" className="h-full w-full rounded-lg object-cover border" />
                                            <button
                                                type="button"
                                                onClick={() => setReviewPhotos(p => p.filter((_, j) => j !== i))}
                                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white flex items-center justify-center shadow-sm"
                                            ><XIcon className="h-2.5 w-2.5" /></button>
                                        </div>
                                    ))}
                                    {reviewPhotos.length < 4 && (
                                        <label className={`h-16 w-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors ${reviewUploadingPhoto ? 'opacity-50' : ''}`}>
                                            <Camera className="h-5 w-5 text-muted-foreground" />
                                            <span className="text-[9px] text-muted-foreground mt-0.5">Foto</span>
                                            <input type="file" accept="image/*" className="hidden" onChange={handleReviewPhotoUpload} disabled={reviewUploadingPhoto} />
                                        </label>
                                    )}
                                    {reviewUploadingPhoto && <span className="text-xs text-muted-foreground self-center">Subiendo…</span>}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {reviewStep === 2 && (
                            <Button variant="outline" onClick={() => setReviewStep(1)} data-testid="review-back">Atrás</Button>
                        )}
                        <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancelar</Button>
                        {reviewStep === 2 && (
                            <Button onClick={submitReview} className="bg-red-600 hover:bg-red-700" data-testid="review-submit">Publicar reseña</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase 7 modals */}
            <ReportModal open={reportOpen} onOpenChange={setReportOpen} targetType="ad" targetId={ad?.id} targetTitle={ad?.title} />
            <MakeOfferModal open={offerOpen} onOpenChange={setOfferOpen} ad={ad} />
            <AddedToCartModal open={cartModalOpen} onClose={() => setCartModalOpen(false)} ad={ad} />
            <SaveToListModal open={listModalOpen} onClose={() => setListModalOpen(false)} adId={ad?.id} />
            {ad?.seller?.id && (
                <ChatDrawer
                    open={chatOpen}
                    onOpenChange={setChatOpen}
                    mode="direct"
                    toUserId={ad.seller.id}
                    adId={ad.id}
                    counterpartLabel={ad.seller.nickname}
                    title={`Chat con @${ad.seller.nickname}`}
                />
            )}
        </div>
    );
}

const AnswerBox = ({ qid, status, onDone }) => {
    const [t, setT] = useState('');
    return (
        <div className="mt-2 space-y-2">
            <Textarea placeholder="Escribe tu respuesta..." value={t} onChange={(e) => setT(e.target.value)} />
            <div className="flex gap-2">
                <Button size="sm" onClick={async () => {
                    if (!t.trim()) return;
                    try { await api.post(`/questions/${qid}/answer`, { text: t.trim() }); toast.success('Respuesta enviada'); setT(''); onDone?.(); }
                    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
                }}>Responder</Button>
                {status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={async () => {
                        try { await api.post(`/questions/${qid}/moderate`, { approve: false }); toast.success('Pregunta rechazada'); onDone?.(); }
                        catch (e) { toast.error('Error'); }
                    }}>Rechazar</Button>
                )}
            </div>
        </div>
    );
};
